import React, { useEffect, useState, useCallback, useRef } from 'react';
import { notesApi } from '@/lib/notesApi';
import {
  X, Plus, FileText, Trash2, Loader2, UserPlus, KeyRound, Ban, CheckCircle2, Users, FolderPlus, Share2, Users2,
  Check, ChevronDown, Eye, Pencil, Wrench, Archive, Download, Upload, AlertTriangle, CalendarClock, Clock, Play, History,
  Building2, ShieldCheck,
} from 'lucide-react';

// ---- Generic scoped modal --------------------------------------------------
// Human-readable byte size for live download progress indicators.
export const fmtBytes = (n) => {
  if (!n || n < 1024) return `${n || 0} B`;
  const u = ['KB', 'MB', 'GB'];
  let v = n / 1024; let i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${u[i]}`;
};

export function Modal({ open, onClose, title, subtitle, children, footer, maxW = 'max-w-md', testid }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid={testid}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`relative z-10 w-full ${maxW} rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]`}>
        <div className="flex items-start justify-between px-5 pt-5 pb-3">
          <div>
            <h3 className="font-display text-xl text-[var(--n-ink)]">{title}</h3>
            {subtitle && <p className="text-xs text-[var(--n-muted)] mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="px-5 pb-2 max-h-[70vh] overflow-y-auto n-scroll">{children}</div>
        {footer && <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--n-line)] mt-2">{footer}</div>}
      </div>
    </div>
  );
}

const inputCls = 'n-focus w-full rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]';
export const BtnGhost = (p) => <button {...p} className={`inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] px-3.5 py-2 text-sm font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] ${p.className || ''}`} />;
export const BtnPrimary = (p) => <button {...p} className={`inline-flex items-center gap-1.5 rounded-xl bg-[var(--n-ink)] px-3.5 py-2 text-sm font-semibold text-[var(--n-canvas)] hover:opacity-90 disabled:opacity-50 ${p.className || ''}`} />;

// ---- Folder create/rename --------------------------------------------------
export const FOLDER_COLORS = [
  { name: 'None', value: null },
  { name: 'Gold', value: '#B8892E' },
  { name: 'Terracotta', value: '#C06B4F' },
  { name: 'Sage', value: '#6E8B6A' },
  { name: 'Teal', value: '#3E8E8E' },
  { name: 'Indigo', value: '#5B6CC0' },
  { name: 'Plum', value: '#8A5C8F' },
  { name: 'Rose', value: '#C05B7A' },
];

export function FolderDialog({ open, onClose, initial, onSubmit }) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(null);
  useEffect(() => { if (open) { setName(initial?.name || ''); setColor(initial?.color || null); } }, [open, initial]);
  const submit = () => { if (name.trim()) onSubmit(name.trim(), color); };
  return (
    <Modal open={open} onClose={onClose} title={initial ? 'Rename folder' : 'New folder'} maxW="max-w-sm" testid="folder-dialog"
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary disabled={!name.trim()} onClick={submit} data-testid="folder-save-button">{initial ? 'Save' : 'Create'}</BtnPrimary></>}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Folder name"
        onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) submit(); }}
        className={inputCls} data-testid="folder-name-input" />
      <div className="mt-4">
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Colour</div>
        <div className="flex flex-wrap gap-2" data-testid="folder-color-picker">
          {FOLDER_COLORS.map((c) => {
            const active = (color || null) === c.value;
            return (
              <button key={c.name} type="button" title={c.name} onClick={() => setColor(c.value)} data-testid="folder-color-swatch"
                className={`h-7 w-7 rounded-full flex items-center justify-center ring-2 ring-offset-2 ring-offset-[var(--n-surface)] transition-transform ${active ? 'ring-[var(--n-accent)] scale-110' : 'ring-transparent hover:scale-105'}`}
                style={{ background: c.value || 'var(--n-canvas-2)' }}>
                {!c.value && <X size={12} className="text-[var(--n-muted)]" />}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// ---- Template picker (on New Note) -----------------------------------------
export function TemplatePickerDialog({ open, onClose, onPick }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    notesApi.get('/notes/templates').then(({ data }) => setTemplates(data || [])).catch(() => setTemplates([])).finally(() => setLoading(false));
  }, [open]);
  return (
    <Modal open={open} onClose={onClose} title="New note" subtitle="Start blank or from a template" maxW="max-w-lg" testid="template-picker-dialog">
      <div className="grid grid-cols-2 gap-3 py-2">
        <button onClick={() => onPick(null)} data-testid="template-pick-blank"
          className="group flex flex-col items-start gap-2 rounded-xl border border-[var(--n-border)] p-4 text-left hover:border-[var(--n-accent-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--n-canvas-2)] text-[var(--n-ink-soft)]"><Plus size={18} /></span>
          <span className="font-display text-base">Blank note</span>
          <span className="text-xs text-[var(--n-muted)]">A clean canvas to start writing.</span>
        </button>
        {loading && <div className="col-span-2 flex justify-center py-6 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div>}
        {!loading && templates.map((t) => (
          <button key={t.id} onClick={() => onPick(t)} data-testid="template-pick-item"
            className="group flex flex-col items-start gap-2 rounded-xl border border-[var(--n-border)] p-4 text-left hover:border-[var(--n-accent-soft)] hover:bg-[var(--n-canvas-2)] transition-colors">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><FileText size={18} /></span>
            <span className="font-display text-base line-clamp-1">{t.name}</span>
            <span className="text-xs text-[var(--n-muted)] line-clamp-2">{t.title || 'Template'}</span>
          </button>
        ))}
        {!loading && templates.length === 0 && (
          <div className="col-span-2 text-center text-xs text-[var(--n-muted)] py-4">No templates yet. Create one from any note via “Save as template”.</div>
        )}
      </div>
    </Modal>
  );
}

// ---- Template manager ------------------------------------------------------
export function TemplateManagerDialog({ open, onClose, currentNote, onChanged }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const load = () => { setLoading(true); notesApi.get('/notes/templates').then(({ data }) => setTemplates(data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { if (open) { load(); setName(''); } }, [open]);

  const saveCurrent = async () => {
    if (!name.trim()) return;
    await notesApi.post('/notes/templates', {
      name: name.trim(),
      title: currentNote?.title && currentNote.title !== 'Untitled' ? currentNote.title : '',
      body_html: currentNote?.body_html || '',
      tags: currentNote?.tags || [],
    });
    setName(''); load(); onChanged?.();
  };
  const del = async (id) => { await notesApi.delete(`/notes/templates/${id}`); load(); onChanged?.(); };

  return (
    <Modal open={open} onClose={onClose} title="Templates" subtitle="Reusable starting points for new notes" maxW="max-w-lg" testid="template-manager-dialog">
      <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3 mb-3">
        <div className="text-xs font-semibold text-[var(--n-ink-soft)] mb-2">Save the current note as a template</div>
        <div className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={currentNote ? 'Template name…' : 'Open a note first'} disabled={!currentNote} className={inputCls} data-testid="template-name-input" />
          <BtnPrimary onClick={saveCurrent} disabled={!currentNote || !name.trim()} data-testid="template-save-current"><Plus size={15} /> Save</BtnPrimary>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-6 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div> : (
        <div className="space-y-1.5 pb-2">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center gap-3 rounded-xl border border-[var(--n-line)] px-3 py-2.5">
              <FileText size={16} className="text-[var(--n-accent)]" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[var(--n-ink)] truncate">{t.name}</div>
                <div className="text-xs text-[var(--n-muted)] truncate">{t.title || 'Untitled starter'} {t.tags?.length ? `· ${t.tags.map((x) => '#' + x).join(' ')}` : ''}</div>
              </div>
              <button onClick={() => del(t.id)} className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)]" aria-label="Delete template" data-testid="template-delete-button"><Trash2 size={15} /></button>
            </div>
          ))}
          {templates.length === 0 && <div className="text-center text-xs text-[var(--n-muted)] py-6">No templates yet.</div>}
        </div>
      )}
    </Modal>
  );
}

// ---- Admin: Notes users (Super Admin only) ---------------------------------
export function AdminUsersDialog({ open, onClose }) {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);   // [{id,name}] for Tag Workspace binding
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', company_id: '', is_tag_admin: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState(null); // user id whose name is being edited
  const [editName, setEditName] = useState('');
  const [resetId, setResetId] = useState(null);     // user id whose password reset panel is open
  const [resetPwVal, setResetPwVal] = useState('');
  const [rowBusy, setRowBusy] = useState(false);
  const [rowMsg, setRowMsg] = useState(null);        // {id, text, kind:'ok'|'err'}
  const flash = (id, text, kind) => { setRowMsg({ id, text, kind }); setTimeout(() => setRowMsg((m) => (m && m.id === id ? null : m)), 2600); };
  const load = () => { setLoading(true); notesApi.get('/notes/admin/users').then(({ data }) => setUsers(data || [])).catch(() => {}).finally(() => setLoading(false)); };
  const loadCompanies = () => { notesApi.get('/notes/admin/companies').then(({ data }) => setCompanies(data || [])).catch(() => setCompanies([])); };
  const companyNameById = (cid) => (companies.find((c) => c.id === cid)?.name) || null;
  useEffect(() => { if (open) { load(); loadCompanies(); setForm({ email: '', name: '', password: '', company_id: '', is_tag_admin: false }); setErr(''); setEditingId(null); setResetId(null); setResetPwVal(''); setRowMsg(null); loadSchedule(); loadHistory(); setSchedPw(''); setSchedMsg(null); } }, [open]);

  const create = async () => {
    setErr(''); setBusy(true);
    try {
      await notesApi.post('/notes/admin/users', {
        email: form.email.trim(), name: form.name.trim(), password: form.password,
        company_id: form.company_id || null, is_tag_admin: !!form.is_tag_admin,
      });
      setForm({ email: '', name: '', password: '', company_id: '', is_tag_admin: false }); load();
    } catch (e) { setErr(e?.response?.data?.detail || 'Could not create user'); } finally { setBusy(false); }
  };
  const toggleDisabled = async (u) => { await notesApi.patch(`/notes/admin/users/${u.id}`, { disabled: !u.disabled }); load(); };
  // Tag Workspace controls — persist immediately on change.
  const setUserCompany = async (u, companyId) => {
    setRowBusy(true);
    try {
      await notesApi.patch(`/notes/admin/users/${u.id}`, { company_id: companyId || '' });
      flash(u.id, companyId ? `Linked to ${companyNameById(companyId) || 'company'}` : 'Company link removed', 'ok');
      load();
    } catch (e) { flash(u.id, e?.response?.data?.detail || 'Could not update company', 'err'); }
    finally { setRowBusy(false); }
  };
  const setUserTagAdmin = async (u, next) => {
    setRowBusy(true);
    try {
      await notesApi.patch(`/notes/admin/users/${u.id}`, { is_tag_admin: !!next });
      flash(u.id, next ? 'Tag Admin enabled' : 'Tag Admin disabled', 'ok');
      load();
    } catch (e) { flash(u.id, e?.response?.data?.detail || 'Could not update Tag Admin', 'err'); }
    finally { setRowBusy(false); }
  };
  const startEdit = (u) => { setEditingId(u.id); setEditName(u.name || ''); setResetId(null); };
  const saveName = async (u) => {
    const name = editName.trim();
    if (!name || name === u.name) { setEditingId(null); return; }
    setRowBusy(true);
    try { await notesApi.patch(`/notes/admin/users/${u.id}`, { name }); setEditingId(null); flash(u.id, 'Name updated', 'ok'); load(); }
    catch (e) { flash(u.id, e?.response?.data?.detail || 'Could not update name', 'err'); }
    finally { setRowBusy(false); }
  };
  const startReset = (u) => { setResetId(u.id); setResetPwVal(''); setEditingId(null); };
  const saveReset = async (u) => {
    if ((resetPwVal || '').length < 6) { flash(u.id, 'Password must be at least 6 characters', 'err'); return; }
    setRowBusy(true);
    try { await notesApi.post(`/notes/admin/users/${u.id}/reset-password`, { password: resetPwVal }); setResetId(null); setResetPwVal(''); flash(u.id, 'Password reset', 'ok'); }
    catch (e) { flash(u.id, e?.response?.data?.detail || 'Could not reset password', 'err'); }
    finally { setRowBusy(false); }
  };

  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const runCleanup = async () => {
    setCleaning(true); setCleanResult(null);
    try {
      const { data } = await notesApi.post('/notes/admin/maintenance/orphan-shares');
      setCleanResult({ ok: true, ...data });
    } catch (e) {
      setCleanResult({ ok: false, msg: e?.response?.data?.detail || 'Cleanup failed' });
    } finally { setCleaning(false); }
  };

  // --- Backup & Restore -----------------------------------------------------
  const [bkPw, setBkPw] = useState('');
  const [bkBusy, setBkBusy] = useState(false);
  const [bkMsg, setBkMsg] = useState(null);       // {text, kind}
  const [bkBytes, setBkBytes] = useState(0);
  const [rsFile, setRsFile] = useState(null);
  const [rsPw, setRsPw] = useState('');
  const [rsMode, setRsMode] = useState('merge');   // 'merge' | 'replace'
  const [rsBusy, setRsBusy] = useState(false);
  const [rsResult, setRsResult] = useState(null);  // summary | {error}

  const doBackup = async () => {
    if (bkPw.length < 6) { setBkMsg({ text: 'Password must be at least 6 characters', kind: 'err' }); return; }
    setBkBusy(true); setBkMsg(null); setBkBytes(0);
    try {
      const res = await notesApi.post('/notes/admin/backup', { password: bkPw }, {
        responseType: 'blob',
        onDownloadProgress: (e) => setBkBytes(e.loaded || 0),
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url; a.download = `notes-backup-${ts}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setBkMsg({ text: 'Backup downloaded. Keep the password safe — it is required to restore.', kind: 'ok' });
    } catch (e) { setBkMsg({ text: e?.response?.data?.detail || 'Backup failed', kind: 'err' }); }
    finally { setBkBusy(false); }
  };

  const doRestore = async () => {
    if (!rsFile) { setRsResult({ error: 'Choose a backup .zip file first.' }); return; }
    if ((rsPw || '').length < 6) { setRsResult({ error: 'Enter the backup password.' }); return; }
    if (rsMode === 'replace' && !window.confirm('REPLACE will permanently erase ALL current Notes data (every user’s notes, folders, tags, shares) and import from this backup. Continue?')) return;
    setRsBusy(true); setRsResult(null);
    try {
      const fd = new FormData();
      fd.append('file', rsFile);
      fd.append('password', rsPw);
      fd.append('mode', rsMode);
      const { data } = await notesApi.post('/notes/admin/restore', fd);
      setRsResult(data); load();
    } catch (e) { setRsResult({ error: e?.response?.data?.detail || 'Restore failed' }); }
    finally { setRsBusy(false); }
  };

  // --- Scheduled backups + history -----------------------------------------
  const WEEKDAYS = [
    { v: 0, l: 'Monday' }, { v: 1, l: 'Tuesday' }, { v: 2, l: 'Wednesday' }, { v: 3, l: 'Thursday' },
    { v: 4, l: 'Friday' }, { v: 5, l: 'Saturday' }, { v: 6, l: 'Sunday' },
  ];
  const [sched, setSched] = useState(null);          // schedule config from server
  const [schedPw, setSchedPw] = useState('');        // new/updated password (optional)
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedMsg, setSchedMsg] = useState(null);    // {text, kind}
  const [runningNow, setRunningNow] = useState(false);
  const [history, setHistory] = useState([]);
  const loadSchedule = () => notesApi.get('/notes/admin/backup-schedule').then(({ data }) => setSched(data)).catch(() => {});
  const loadHistory = () => notesApi.get('/notes/admin/backup-history').then(({ data }) => setHistory(data || [])).catch(() => {});

  const saveSchedule = async (override = {}) => {
    const base = sched || { enabled: false, weekday: 0, hour: 2, include_attachments: true };
    const payload = {
      enabled: base.enabled, weekday: base.weekday, hour: base.hour,
      include_attachments: base.include_attachments, ...override,
    };
    if (schedPw) payload.password = schedPw;
    setSchedBusy(true); setSchedMsg(null);
    try {
      const { data } = await notesApi.put('/notes/admin/backup-schedule', payload);
      setSched(data); setSchedPw('');
      setSchedMsg({ text: 'Schedule saved.', kind: 'ok' });
    } catch (e) {
      setSchedMsg({ text: e?.response?.data?.detail || 'Could not save schedule', kind: 'err' });
    } finally { setSchedBusy(false); }
  };

  const runNow = async () => {
    setRunningNow(true); setSchedMsg(null);
    try {
      await notesApi.post('/notes/admin/backup-run-now');
      setSchedMsg({ text: 'Backup created and saved to server.', kind: 'ok' });
      loadHistory();
    } catch (e) {
      setSchedMsg({ text: e?.response?.data?.detail || 'Could not create backup', kind: 'err' });
    } finally { setRunningNow(false); }
  };

  const downloadHistory = async (h) => {
    try {
      const res = await notesApi.get(`/notes/admin/backup-history/${h.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url; a.download = h.filename || 'notes-backup.zip';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { /* noop */ }
  };
  const deleteHistory = async (h) => {
    if (!window.confirm(`Delete backup “${h.filename}” from the server?`)) return;
    try { await notesApi.delete(`/notes/admin/backup-history/${h.id}`); loadHistory(); } catch { /* noop */ }
  };

  return (
    <Modal open={open} onClose={onClose} title="Notes users" subtitle="Create and manage who can sign in to Notes" maxW="max-w-xl" testid="notes-users-dialog">
      <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3 mb-3">
        <div className="text-xs font-semibold text-[var(--n-ink-soft)] mb-2 flex items-center gap-1.5"><UserPlus size={14} /> Add a Notes user</div>
        {err && <div className="mb-2 text-xs text-[var(--n-danger)]">{err}</div>}
        <div className="grid grid-cols-3 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className={inputCls} data-testid="notes-users-name-input" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="notes-users-email-input" />
          <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password (min 6)" className={inputCls} data-testid="notes-users-password-input" />
        </div>
        {/* Tag Workspace binding — link to a company + optional Tag Admin rights (rosentiquestag.com) */}
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
            <span className="flex items-center gap-1.5"><Building2 size={12} /> Tag Workspace company</span>
            <select
              value={form.company_id}
              onChange={(e) => setForm({ ...form, company_id: e.target.value })}
              className={`${inputCls} font-normal normal-case`}
              data-testid="notes-users-company-select"
            >
              <option value="">No company (own tags only)</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <div className="flex items-end">
            <button
              type="button" role="switch" aria-checked={!!form.is_tag_admin}
              onClick={() => setForm({ ...form, is_tag_admin: !form.is_tag_admin })}
              data-testid="notes-users-tag-admin-toggle"
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5"
            >
              <span className="flex items-center gap-1.5 text-sm text-[var(--n-ink-soft)]"><ShieldCheck size={14} className={form.is_tag_admin ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)]'} /> Tag Admin</span>
              <span className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${form.is_tag_admin ? 'bg-[var(--n-accent)]' : 'bg-[var(--n-muted-2)]/50'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_tag_admin ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </span>
            </button>
          </div>
        </div>
        <p className="mt-1.5 text-[10.5px] leading-relaxed text-[var(--n-muted-2)]">
          A company link lets this user manage that company&rsquo;s tags on the tag workspace. <strong className="font-semibold text-[var(--n-ink-soft)]">Tag Admin</strong> can create, edit &amp; delete manual tags; others can only create.
        </p>
        <div className="flex justify-end mt-2">
          <BtnPrimary onClick={create} disabled={busy || !form.email || !form.name || form.password.length < 6} data-testid="notes-users-create-button">{busy ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Create</BtnPrimary>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-6 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div> : (
        <div className="space-y-1.5 pb-2" data-testid="notes-users-table">
          {users.map((u) => (
            <div key={u.id} className="rounded-xl border border-[var(--n-line)] px-3 py-2.5" data-testid="notes-user-row">
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-accent)] text-xs font-bold">{(u.name || u.email).slice(0, 2).toUpperCase()}</span>
                <div className="min-w-0 flex-1">
                  {editingId === u.id ? (
                    <div className="flex items-center gap-2">
                      <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveName(u); else if (e.key === 'Escape') setEditingId(null); }}
                        data-testid="notes-user-name-edit-input" placeholder="Full name"
                        className="n-focus min-w-0 flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-sm text-[var(--n-ink)]" />
                      <button onClick={() => saveName(u)} disabled={rowBusy} title="Save name" data-testid="notes-user-name-save" className="text-[var(--n-accent)] hover:opacity-80">{rowBusy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}</button>
                      <button onClick={() => setEditingId(null)} title="Cancel" className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={16} /></button>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-[var(--n-ink)] truncate" data-testid="notes-user-name">{u.name} {u.disabled && <span className="text-[10px] text-[var(--n-danger)] font-semibold">· disabled</span>}</div>
                      <div className="text-xs text-[var(--n-muted)] truncate">{u.email}</div>
                    </>
                  )}
                </div>
                {editingId !== u.id && (
                  <div className="flex shrink-0 items-center gap-2.5">
                    <button onClick={() => startEdit(u)} title="Edit name" data-testid="notes-user-edit-name" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><Pencil size={15} /></button>
                    <button onClick={() => startReset(u)} title="Reset password" data-testid="notes-user-reset-password" className={`hover:text-[var(--n-ink)] ${resetId === u.id ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)]'}`}><KeyRound size={15} /></button>
                    <button onClick={() => toggleDisabled(u)} title={u.disabled ? 'Enable' : 'Disable'} data-testid="notes-user-toggle-disabled" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]">{u.disabled ? <CheckCircle2 size={15} /> : <Ban size={15} />}</button>
                  </div>
                )}
              </div>
              {/* Tag Workspace binding (rosentiquestag.com) — company link + Tag Admin rights */}
              {editingId !== u.id && (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-[var(--n-line)] bg-[var(--n-canvas-2)] px-2 py-1.5" data-testid="notes-user-tagws-controls">
                  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]"><Building2 size={11} /> Company</span>
                  <select
                    value={u.company_id || ''}
                    disabled={rowBusy}
                    onChange={(e) => setUserCompany(u, e.target.value)}
                    data-testid={`notes-user-company-select-${u.id}`}
                    className="n-focus min-w-[140px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[12px] text-[var(--n-ink)]"
                  >
                    <option value="">No company</option>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button
                    type="button" role="switch" aria-checked={!!u.is_tag_admin} disabled={rowBusy}
                    onClick={() => setUserTagAdmin(u, !u.is_tag_admin)}
                    data-testid={`notes-user-tag-admin-toggle-${u.id}`}
                    title={u.is_tag_admin ? 'Tag Admin — can edit & delete manual tags' : 'Make Tag Admin'}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${u.is_tag_admin ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}
                  >
                    <ShieldCheck size={12} /> Tag Admin
                  </button>
                </div>
              )}
              {resetId === u.id && (
                <div className="mt-2 flex items-center gap-2 rounded-lg bg-[var(--n-canvas-2)] p-2" data-testid="notes-user-reset-panel">
                  <KeyRound size={14} className="shrink-0 text-[var(--n-muted-2)]" />
                  <input type="password" autoFocus value={resetPwVal} placeholder="New password (min 6)" data-testid="notes-user-reset-input"
                    onChange={(e) => setResetPwVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveReset(u); else if (e.key === 'Escape') { setResetId(null); setResetPwVal(''); } }}
                    className="n-focus min-w-0 flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-sm text-[var(--n-ink)]" />
                  <BtnPrimary onClick={() => saveReset(u)} disabled={rowBusy || resetPwVal.length < 6} data-testid="notes-user-reset-save">{rowBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Set</BtnPrimary>
                  <button onClick={() => { setResetId(null); setResetPwVal(''); }} title="Cancel" className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={16} /></button>
                </div>
              )}
              {rowMsg && rowMsg.id === u.id && (
                <div className={`mt-1.5 text-[11.5px] font-medium ${rowMsg.kind === 'ok' ? 'text-[var(--n-accent)]' : 'text-[var(--n-danger)]'}`} data-testid="notes-user-row-msg">{rowMsg.text}</div>
              )}
            </div>
          ))}
          {users.length === 0 && <div className="text-center text-xs text-[var(--n-muted)] py-6 flex flex-col items-center gap-2"><Users size={20} className="opacity-50" /> No Notes users yet.</div>}
        </div>
      )}
      {/* Maintenance — clean up orphaned shares left by pre-fix folder/note deletions */}
      <div className="mt-3 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3" data-testid="notes-maintenance-section">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><Wrench size={14} /> Maintenance</div>
        <p className="mb-2.5 text-[11.5px] leading-relaxed text-[var(--n-muted)]">
          Remove leftover share records that point to folders or notes which no longer exist. This makes ghost
          &ldquo;Shared with me&rdquo; items disappear for affected users right away.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <BtnPrimary onClick={runCleanup} disabled={cleaning} data-testid="notes-cleanup-orphan-shares-button">
            {cleaning ? <Loader2 size={15} className="animate-spin" /> : <Wrench size={15} />} Clean up orphaned shares
          </BtnPrimary>
          {cleanResult && cleanResult.ok && (
            <span className="text-[12px] font-medium text-[var(--n-accent)]" data-testid="notes-cleanup-result">
              Removed {cleanResult.total_removed} share{cleanResult.total_removed === 1 ? '' : 's'}
              {` (${cleanResult.folders_removed} folder, ${cleanResult.notes_removed} note) · ${cleanResult.users_notified} user${cleanResult.users_notified === 1 ? '' : 's'} refreshed`}
            </span>
          )}
          {cleanResult && !cleanResult.ok && (
            <span className="text-[12px] font-medium text-[var(--n-danger)]" data-testid="notes-cleanup-error">{cleanResult.msg}</span>
          )}
        </div>
      </div>
      {/* Backup & Restore — password-protected ZIP of all Notes data + attachments */}
      <div className="mt-3 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3" data-testid="notes-backup-section">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><Archive size={14} /> Backup &amp; Restore</div>
        <p className="mb-2.5 text-[11.5px] leading-relaxed text-[var(--n-muted)]">
          Download a password-protected ZIP of every Notes user&rsquo;s folders, notes, tags, templates, shares and
          attachments. Keep the password safe — it is required to restore.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input type="password" value={bkPw} onChange={(e) => setBkPw(e.target.value)} placeholder="Set a backup password (min 6)" data-testid="notes-backup-password-input"
            className="n-focus min-w-[200px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-sm text-[var(--n-ink)]" />
          <BtnPrimary onClick={doBackup} disabled={bkBusy || bkPw.length < 6} data-testid="notes-backup-download-button">
            {bkBusy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download backup
          </BtnPrimary>
        </div>
        {bkBusy && (
          <div className="mt-2" data-testid="notes-backup-progress">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--n-border)]">
              <div className="h-full w-1/3 rounded-full bg-[var(--n-accent)] n-indeterminate" />
            </div>
            <div className="mt-1 text-[11px] font-medium text-[var(--n-muted)]" data-testid="notes-backup-progress-text">
              Preparing &amp; streaming… {fmtBytes(bkBytes)} downloaded
            </div>
          </div>
        )}
        {bkMsg && <div className={`mt-1.5 text-[11.5px] font-medium ${bkMsg.kind === 'ok' ? 'text-[var(--n-accent)]' : 'text-[var(--n-danger)]'}`} data-testid="notes-backup-msg">{bkMsg.text}</div>}

        <div className="my-3 h-px bg-[var(--n-line)]" />

        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><Upload size={14} /> Restore from backup</div>
        <div className="flex flex-col gap-2">
          <input type="file" accept=".zip" data-testid="notes-restore-file-input"
            onChange={(e) => { setRsFile(e.target.files?.[0] || null); setRsResult(null); }}
            className="text-[12px] text-[var(--n-muted)] file:mr-2 file:rounded-lg file:border-0 file:bg-[var(--n-accent-bg)] file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-[var(--n-accent)] hover:file:opacity-90" />
          <div className="flex flex-wrap items-center gap-2">
            <input type="password" value={rsPw} onChange={(e) => setRsPw(e.target.value)} placeholder="Backup password" data-testid="notes-restore-password-input"
              className="n-focus min-w-[160px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-sm text-[var(--n-ink)]" />
            <div className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5" data-testid="notes-restore-mode">
              {[['merge', 'Merge'], ['replace', 'Replace']].map(([val, lbl]) => (
                <button key={val} onClick={() => setRsMode(val)} data-testid={`notes-restore-mode-${val}`}
                  className={`n-focus rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${rsMode === val ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>{lbl}</button>
              ))}
            </div>
            <BtnPrimary onClick={doRestore} disabled={rsBusy || !rsFile || rsPw.length < 6} data-testid="notes-restore-button">
              {rsBusy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Restore
            </BtnPrimary>
          </div>
          <div className={`flex items-start gap-1.5 text-[11px] ${rsMode === 'replace' ? 'text-[var(--n-danger)]' : 'text-[var(--n-muted-2)]'}`}>
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            {rsMode === 'replace'
              ? 'Replace erases ALL current Notes data for every user, then imports from the backup.'
              : 'Merge keeps current data and adds only records missing from it.'}
          </div>
        </div>
        {rsResult && rsResult.error && <div className="mt-2 text-[11.5px] font-medium text-[var(--n-danger)]" data-testid="notes-restore-error">{rsResult.error}</div>}
        {rsResult && !rsResult.error && (
          <div className="mt-2 rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] p-2 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="notes-restore-result">
            <div className="mb-1 font-semibold text-[var(--n-accent)]">Restore complete ({rsResult.mode})</div>
            {Object.entries(rsResult.collections || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-[var(--n-muted)]">{k.replace('notes_', '')}</span>
                <span className="tabular-nums">{v.replaced != null ? `${v.replaced} replaced` : `${v.added} added · ${v.skipped} skipped`}</span>
              </div>
            ))}
            <div className="mt-1 text-[var(--n-muted)]">Attachments restored: {rsResult.attachments_restored}{rsResult.attachment_errors ? ` (${rsResult.attachment_errors} failed)` : ''}</div>
          </div>
        )}
      </div>

      {/* Scheduled backups — weekly auto-backup to server disk + history */}
      <div className="mt-3 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3" data-testid="notes-schedule-section">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><CalendarClock size={14} /> Scheduled backups</div>
        <p className="mb-2.5 text-[11.5px] leading-relaxed text-[var(--n-muted)]">
          Automatically create an encrypted Notes backup once a week and keep it on the server. Backups appear in the history below to download anytime.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" role="switch" aria-checked={!!sched?.enabled} data-testid="notes-schedule-toggle"
            onClick={() => saveSchedule({ enabled: !sched?.enabled })} disabled={schedBusy}
            className={`n-focus relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${sched?.enabled ? 'bg-[var(--n-accent)]' : 'bg-[var(--n-muted-2)]/50'}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${sched?.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-[13px] font-medium text-[var(--n-ink-soft)]">{sched?.enabled ? 'Weekly backups on' : 'Weekly backups off'}</span>
        </div>

        <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
            Day of week
            <select value={sched?.weekday ?? 0} data-testid="notes-schedule-weekday"
              onChange={(e) => saveSchedule({ weekday: parseInt(e.target.value, 10) })} disabled={schedBusy}
              className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[13px] font-normal normal-case text-[var(--n-ink)]">
              {WEEKDAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
            Time (IST)
            <select value={sched?.hour ?? 2} data-testid="notes-schedule-hour"
              onChange={(e) => saveSchedule({ hour: parseInt(e.target.value, 10) })} disabled={schedBusy}
              className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[13px] font-normal normal-case text-[var(--n-ink)]">
              {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </label>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <input type="password" value={schedPw} onChange={(e) => setSchedPw(e.target.value)} data-testid="notes-schedule-password"
            placeholder={sched?.has_password ? 'Change backup password (optional)' : 'Set a backup password (min 6)'}
            className="n-focus min-w-[200px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-sm text-[var(--n-ink)]" />
          <BtnPrimary onClick={() => saveSchedule()} disabled={schedBusy} data-testid="notes-schedule-save">
            {schedBusy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
          </BtnPrimary>
          <BtnGhost onClick={runNow} disabled={runningNow || !sched?.has_password} data-testid="notes-schedule-run-now" title={!sched?.has_password ? 'Set and save a password first' : 'Create a backup now'}>
            {runningNow ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run now
          </BtnGhost>
        </div>
        {sched?.has_password && <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-[var(--n-muted-2)]"><KeyRound size={12} /> A backup password is saved.{sched?.last_run_at ? ` Last run: ${new Date(sched.last_run_at).toLocaleString()}.` : ''}</div>}
        {schedMsg && <div className={`mt-1.5 text-[11.5px] font-medium ${schedMsg.kind === 'ok' ? 'text-[var(--n-accent)]' : 'text-[var(--n-danger)]'}`} data-testid="notes-schedule-msg">{schedMsg.text}</div>}

        <div className="my-3 h-px bg-[var(--n-line)]" />
        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-[var(--n-ink-soft)]"><History size={14} /> Backup history</div>
        {history.length === 0 ? (
          <p className="text-[12px] text-[var(--n-muted-2)]" data-testid="notes-backup-history-empty">No saved backups yet.</p>
        ) : (
          <div className="space-y-1.5" data-testid="notes-backup-history">
            {history.map((h) => (
              <div key={h.id} data-testid="notes-backup-history-row"
                className="flex items-center gap-2 rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] px-2.5 py-2">
                <Archive size={14} className="shrink-0 text-[var(--n-accent)]" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-medium text-[var(--n-ink)]">{h.filename}</div>
                  <div className="flex items-center gap-2 text-[11px] text-[var(--n-muted-2)]">
                    <span className="inline-flex items-center gap-1"><Clock size={10} />{new Date(h.created_at).toLocaleString()}</span>
                    <span>· {(h.size / 1048576).toFixed(2)} MB</span>
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${h.trigger === 'scheduled' ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'bg-[var(--n-canvas-2)] text-[var(--n-muted)]'}`}>{h.trigger === 'scheduled' ? 'Scheduled' : 'Manual'}</span>
                  </div>
                </div>
                <button onClick={() => downloadHistory(h)} title="Download" data-testid="notes-backup-history-download"
                  className="n-focus grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]"><Download size={15} /></button>
                <button onClick={() => deleteHistory(h)} title="Delete" data-testid="notes-backup-history-delete"
                  className="n-focus grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-danger)]/10 hover:text-[var(--n-danger)]"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---- Share dialog (in-app sharing with directory users) --------------------
const PERM_OPTS = [
  { value: 'view', label: 'Can view', icon: Eye },
  { value: 'edit', label: 'Can edit', icon: Pencil },
];

// Compact View/Edit permission picker used both when adding and per-person.
function PermMenu({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const cur = PERM_OPTS.find((p) => p.value === value) || PERM_OPTS[0];
  const CurIcon = cur.icon;
  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid}
        className="n-focus inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
        <CurIcon size={13} /> {cur.label} <ChevronDown size={13} />
      </button>
      {open && (
        <div className="absolute right-0 z-[60] mt-1 w-36 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]">
          {PERM_OPTS.map((p) => {
            const Icon = p.icon;
            return (
              <button key={p.value} type="button" onClick={() => { onChange(p.value); setOpen(false); }} data-testid={`${testid}-opt-${p.value}`}
                className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-[var(--n-canvas-2)] ${value === p.value ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
                <Icon size={13} /> {p.label} {value === p.value && <Check size={12} className="ml-auto" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ShareDialog({ open, onClose, resource, notify }) {
  const [directory, setDirectory] = useState([]);
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState([]); // user ids queued to add
  const [permission, setPermission] = useState('view');
  const [busy, setBusy] = useState(false);
  const pickerRef = useRef(null);

  const rtype = resource?.type; // 'note' | 'folder'
  const rid = resource?.id;

  const load = useCallback(async () => {
    if (!rtype || !rid) return;
    setLoading(true);
    try {
      const [dir, sh] = await Promise.all([
        notesApi.get('/notes/directory'),
        notesApi.get(`/notes/shares?resource_type=${rtype}&resource_id=${rid}`),
      ]);
      setDirectory(dir.data || []);
      setShares(sh.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [rtype, rid]);

  useEffect(() => { if (open) { setSelected([]); setPermission('view'); setPickerOpen(false); load(); } }, [open, load]);

  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const sharedIds = new Set(shares.map((s) => s.target_user_id));
  const available = directory.filter((u) => !sharedIds.has(u.id));
  const toggleSelect = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const initials = (u) => (u.name || u.email || '?').slice(0, 1).toUpperCase();
  const selectedLabel = selected.length ? `${selected.length} selected` : 'Select people…';

  const addShares = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      for (const uid of selected) {
        await notesApi.post('/notes/shares', { resource_type: rtype, resource_id: rid, target_user_id: uid, permission });
      }
      const n = selected.length;
      setSelected([]); setPickerOpen(false);
      await load();
      notify?.(`Shared with ${n} ${n === 1 ? 'person' : 'people'}.`);
    } catch (e) { notify?.(e?.response?.data?.detail || 'Could not share.'); } finally { setBusy(false); }
  };

  const changePerm = async (uid, perm) => {
    setShares((p) => p.map((s) => (s.target_user_id === uid ? { ...s, permission: perm } : s)));
    try { await notesApi.post('/notes/shares', { resource_type: rtype, resource_id: rid, target_user_id: uid, permission: perm }); }
    catch { notify?.('Could not update permission.'); load(); }
  };

  const removeShare = async (uid) => {
    setShares((p) => p.filter((s) => s.target_user_id !== uid));
    try { await notesApi.delete(`/notes/shares?resource_type=${rtype}&resource_id=${rid}&target_user_id=${uid}`); }
    catch { notify?.('Could not remove access.'); load(); }
  };

  return (
    <Modal open={open} onClose={onClose} maxW="max-w-lg" testid="notes-share-dialog"
      title={`Share ${rtype === 'folder' ? 'folder' : 'note'}`}
      subtitle={resource?.name ? `“${resource.name}”` : 'Choose who can access this'}>
      <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)] p-3 mb-3">
        <div className="text-xs font-semibold text-[var(--n-ink-soft)] mb-2 flex items-center gap-1.5"><Users2 size={14} /> Add people</div>
        <div className="flex items-center gap-2">
          <div className="relative flex-1" ref={pickerRef}>
            <button type="button" onClick={() => setPickerOpen((o) => !o)} data-testid="share-people-picker-button"
              className="n-focus flex w-full items-center justify-between gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm hover:bg-[var(--n-canvas-2)]">
              <span className={`truncate ${selected.length ? 'text-[var(--n-ink)]' : 'text-[var(--n-muted-2)]'}`}>{selectedLabel}</span>
              <ChevronDown size={15} className="shrink-0 text-[var(--n-muted-2)]" />
            </button>
            {pickerOpen && (
              <div className="absolute left-0 right-0 z-50 mt-1 max-h-56 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="share-people-picker-menu">
                {available.length === 0 ? (
                  <div className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted)]">Everyone already has access.</div>
                ) : available.map((u) => {
                  const on = selected.includes(u.id);
                  return (
                    <button key={u.id} type="button" onClick={() => toggleSelect(u.id)} data-testid="share-people-option"
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] ${on ? 'bg-[var(--n-accent-bg)]' : ''}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)] text-transparent'}`}><Check size={12} /></span>
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{initials(u)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-[var(--n-ink)]">{u.name}</span>
                        <span className="block truncate text-[11px] text-[var(--n-muted)]">{u.email}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <PermMenu value={permission} onChange={setPermission} testid="share-add-permission" />
        </div>
        <div className="mt-2 flex justify-end">
          <BtnPrimary onClick={addShares} disabled={busy || !selected.length} data-testid="share-add-button">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} Share
          </BtnPrimary>
        </div>
      </div>

      <div className="mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">People with access</div>
      {loading ? (
        <div className="flex justify-center py-6 text-[var(--n-muted)]"><Loader2 className="animate-spin" size={18} /></div>
      ) : (
        <div className="space-y-1.5 pb-2" data-testid="share-current-list">
          {shares.length === 0 && <div className="text-center text-xs text-[var(--n-muted)] py-5">Not shared with anyone yet.</div>}
          {shares.map((s) => (
            <div key={s.target_user_id} className="flex items-center gap-3 rounded-xl border border-[var(--n-line)] px-3 py-2.5" data-testid="share-current-item">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-xs font-bold text-[var(--n-accent)]">{initials(s)}</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-[var(--n-ink)]">{s.name}</div>
                <div className="truncate text-xs text-[var(--n-muted)]">{s.email}</div>
              </div>
              <PermMenu value={s.permission} onChange={(v) => changePerm(s.target_user_id, v)} testid="share-item-permission" />
              <button onClick={() => removeShare(s.target_user_id)} title="Remove access" aria-label="Remove access"
                data-testid="share-remove-button" className="text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
